import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  FileText,
  File,
  Wrench,
  Settings,
  Users,
  X,
  Shield,
  Car,
  DollarSign,
  Bell,
  AlertTriangle,
  MessageSquareWarning,
  SlidersHorizontal,
  Search,
  Map,
  Building2,
  ShieldCheck,
  Receipt,
  Activity,
  KeyRound,
  BarChart3,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getDashboardStats } from '../services/searchHistoryService';
import { getNavigationSettings, NavigationSettings } from '../services/settingsService';
import { makeAuthenticatedRequest } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Feature flag to show/hide API usage widget
const SHOW_API_USAGE = false;

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();

  // The sidebar is re-mounted on every page (each page renders its own <Sidebar/>), so
  // navigating would reset the menu scroll to the top. Persist + restore the scroll
  // position so clicking a bottom item doesn't jump the menu back up.
  const scrollRef = useRef<HTMLDivElement>(null);
  const SCROLL_KEY = 'aas_sidebar_scroll';

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (saved) el.scrollTop = parseInt(saved, 10) || 0;
  }, []);

  const handleSidebarScroll = (e: React.UIEvent<HTMLDivElement>) => {
    sessionStorage.setItem(SCROLL_KEY, String(e.currentTarget.scrollTop));
  };

  const isClientAdmin = user?.role === 'ClientAdmin';
  const hasPricingAccess = user?.hasPricingAccess === true;
  const isSystemAdmin = user?.isSystemAdmin === true;
  const [apiUsage, setApiUsage] = useState<{ used: number; limit: number } | null>(null);
  const [openProblemCount, setOpenProblemCount] = useState<number>(0);
  const [navSettings, setNavSettings] = useState<NavigationSettings>({
    nmvtisRecordEnabled: true,
    theftLienCheckEnabled: true,
    motorVehicleRecordEnabled: true,
    vinDecoderEnabled: true,
  });

  // Fetch navigation settings
  useEffect(() => {
    const fetchNavSettings = async () => {
      try {
        const settings = await getNavigationSettings();
        setNavSettings(settings);
      } catch (error) {
        console.error('Failed to fetch navigation settings:', error);
      }
    };

    fetchNavSettings();
  }, []);

  // Fetch open problem report count (admin users only)
  useEffect(() => {
    if (!isClientAdmin && !isSystemAdmin) return;

    const fetchOpenCount = async () => {
      try {
        const response = await makeAuthenticatedRequest(API_ENDPOINTS.PROBLEM_REPORTS.ADMIN_OPEN_COUNT);
        if (response.ok) {
          const count = await response.json();
          setOpenProblemCount(count);
        }
      } catch {
        // Silently fail - badge just won't show
      }
    };

    fetchOpenCount();
    const interval = setInterval(fetchOpenCount, 5 * 60 * 1000); // refresh every 5 minutes
    return () => clearInterval(interval);
  }, [isClientAdmin, isSystemAdmin]);

  // Fetch API usage stats
  useEffect(() => {
    const fetchApiUsage = async () => {
      try {
        const stats = await getDashboardStats();
        // Use searchesThisMonth as a proxy for API usage
        // In a real app, you'd have a dedicated endpoint for quota info
        setApiUsage({
          used: stats.searchesThisMonth || 0,
          limit: 10000, // Example limit - adjust based on your plan tiers
        });
      } catch (error) {
        console.error('Failed to fetch API usage:', error);
      }
    };

    if (user) {
      fetchApiUsage();
    }
  }, [user]);

  // Vehicle Reports Section - filtered by both system-wide navigation settings AND per-user permissions
  const vehicleReports: NavItem[] = [
    { name: 'Dashboard', path: '/dashboard', icon: Home },
    { name: 'Reports', path: '/record-inquiry-log', icon: FileText },
    ...(navSettings.nmvtisRecordEnabled && user?.canAccessNmvtis !== false ? [{ name: 'NMVTIS Record', path: '/nmvtis-record', icon: File }] : []),
    ...(navSettings.theftLienCheckEnabled && user?.canAccessTheftLien !== false ? [{ name: 'Theft/Lien Check', path: '/lien-theft-record', icon: Shield }] : []),
    ...(navSettings.motorVehicleRecordEnabled && user?.canAccessMotorVehicleRecord !== false ? [{ name: 'Motor Vehicle Record', path: '/motor-vehicle-record', icon: Car }] : []),
    ...(user?.canAccessAstsPublic !== false ? [{ name: 'ASTS Public Inquiry', path: '/asts-public-inquiry', icon: Search }] : []),
    ...(navSettings.vinDecoderEnabled && user?.canAccessVinDecoder !== false ? [{ name: 'VIN Decoder', path: '/vin-decode', icon: Wrench }] : []),
  ];

  // Settings Section
  const settingsItems: NavItem[] = [
    { name: 'Settings', path: '/settings', icon: Settings },
    // Notification Management (ClientAdmin or SystemAdmin only)
    ...((isClientAdmin || isSystemAdmin) ? [{ name: 'Notifications', path: '/notification-management', icon: Bell }] : []),
  ];

  // Admin Section (ClientAdmin only)
  const adminItems: NavItem[] = [
    { name: 'Organization', path: '/management', icon: Users },
    { name: 'Problem Reports', path: '/admin/problem-reports', icon: AlertTriangle },
  ];

  // SystemAdmin Section (SystemAdmin only)
  const systemAdminItems: NavItem[] = [
    { name: 'Jurisdictions', path: '/admin/jurisdictions', icon: Map },
    { name: 'User Activity', path: '/admin/user-activity', icon: Activity },
    { name: 'State Credentials', path: '/admin/portal-credentials', icon: KeyRound },
    { name: 'App Settings', path: '/admin/application-settings', icon: SlidersHorizontal },
    { name: 'Reports', path: '/admin/reports', icon: BarChart3 },
  ];

  // Pricing Section (hasPricingAccess or SystemAdmin)
  const pricingItems: NavItem[] = [
    { name: 'Pricing', path: '/pricing-management', icon: DollarSign },
    { name: 'Margins', path: '/margins', icon: TrendingUp },
    // 'Client Discounts' retired — superseded by per-client Service Pricing + Per-State
    // Pricing on the client editor. Existing discount rows still apply as a fallback.
  ];

  const canAccessInvoicing = user?.canAccessInvoicing === true;
  const canManageClients = user?.canManageClients === true;

  const isActive = (path: string) => location.pathname === path;

  const handleNavClick = (e: React.MouseEvent) => {
    // On mobile, close sidebar when clicking a nav item
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Close sidebar on ESC key
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Calculate usage percentage
  const usagePercentage = apiUsage ? Math.min((apiUsage.used / apiUsage.limit) * 100, 100) : 0;
  const usageColor = usagePercentage > 80 ? '#dc2626' : usagePercentage > 60 ? '#d97706' : '#c41e3a';

  return (
    <>
      {/* Backdrop - Only visible on mobile/tablet when sidebar is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-200"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        data-tour="nav"
        className={`
          fixed top-0 lg:top-[88px] left-0 h-full lg:h-[calc(100vh-88px)] z-50 lg:z-30
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:fixed
          w-[260px] lg:w-[240px]
          flex flex-col
          bg-white/90 backdrop-blur-xl
          border-r border-black/[0.06]
          shadow-[4px_0_24px_rgba(0,0,0,0.04)]
        `}
        onKeyDown={handleKeyDown}
        aria-label="Main navigation"
      >
        {/* Close Button - Only visible on mobile/tablet */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-black/[0.06]">
          <Link to="/" className="flex items-center no-underline">
            <img src="/aaslogo.png" alt="Auto Access Solutions" className="h-8" />
          </Link>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-black/5 transition-colors"
            aria-label="Close navigation menu"
          >
            <X className="w-5 h-5 text-[#475569]" />
          </button>
        </div>

        {/* Scrollable Navigation Content */}
        <div
          ref={scrollRef}
          onScroll={handleSidebarScroll}
          className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4"
        >
          {/* Main Navigation */}
          <nav className="space-y-1">
            {vehicleReports.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={handleNavClick}
                  className={`
                    flex items-center px-3 py-2.5 rounded-lg
                    text-sm font-medium transition-all duration-200 no-underline
                    ${
                      active
                        ? 'bg-[#c41e3a]/10 text-[#c41e3a] border-l-3 border-[#c41e3a]'
                        : 'text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.04]'
                    }
                  `}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-[#c41e3a]' : ''}`} />
                  <span className="ml-3 truncate">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Divider */}
          <div className="border-t border-black/[0.06] my-4 mx-2" />

          {/* SETTINGS Section */}
          <div>
            <h3 className="px-3 mb-2 text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wider">
              Settings
            </h3>
            <nav className="space-y-1">
              {settingsItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    className={`
                      flex items-center px-3 py-2.5 rounded-lg
                      text-sm font-medium transition-all duration-200 no-underline
                      ${
                        active
                          ? 'bg-[#c41e3a]/10 text-[#c41e3a] border-l-3 border-[#c41e3a]'
                          : 'text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.04]'
                      }
                    `}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-[#c41e3a]' : ''}`} />
                    <span className="ml-3 truncate">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* ADMIN Section - ClientAdmin, SystemAdmin, pricing access, invoicing access, or client-management access */}
          {(isClientAdmin || isSystemAdmin || hasPricingAccess || canAccessInvoicing || canManageClients) && (
            <>
              <div className="border-t border-black/[0.06] my-4 mx-2" />
              <div>
                <h3 className="px-3 mb-2 text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wider">
                  Admin
                </h3>
                <nav className="space-y-1">
                  {(isClientAdmin || isSystemAdmin) && adminItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    const isProblemReports = item.path === '/admin/problem-reports';

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={handleNavClick}
                        className={`
                          flex items-center px-3 py-2.5 rounded-lg
                          text-sm font-medium transition-all duration-200 no-underline
                          ${
                            active
                              ? 'bg-[#c41e3a]/10 text-[#c41e3a] border-l-3 border-[#c41e3a]'
                              : 'text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.04]'
                          }
                        `}
                        aria-current={active ? 'page' : undefined}
                      >
                        <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-[#c41e3a]' : ''}`} />
                        <span className="ml-3 truncate">{item.name}</span>
                        {isProblemReports && openProblemCount > 0 && (
                          <span className="ml-auto flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[#c41e3a] text-white text-[11px] font-semibold flex items-center justify-center">
                            {openProblemCount > 99 ? '99+' : openProblemCount}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                  {(hasPricingAccess || isSystemAdmin) && pricingItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={handleNavClick}
                        className={`
                          flex items-center px-3 py-2.5 rounded-lg
                          text-sm font-medium transition-all duration-200 no-underline
                          ${
                            active
                              ? 'bg-[#c41e3a]/10 text-[#c41e3a] border-l-3 border-[#c41e3a]'
                              : 'text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.04]'
                          }
                        `}
                        aria-current={active ? 'page' : undefined}
                      >
                        <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-[#c41e3a]' : ''}`} />
                        <span className="ml-3 truncate">{item.name}</span>
                      </Link>
                    );
                  })}
                  {(isSystemAdmin || canManageClients) && (
                    <Link
                      to="/admin/clients"
                      onClick={handleNavClick}
                      className={`
                        flex items-center px-3 py-2.5 rounded-lg
                        text-sm font-medium transition-all duration-200 no-underline
                        ${
                          isActive('/admin/clients')
                            ? 'bg-[#c41e3a]/10 text-[#c41e3a] border-l-3 border-[#c41e3a]'
                            : 'text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.04]'
                        }
                      `}
                      aria-current={isActive('/admin/clients') ? 'page' : undefined}
                    >
                      <Building2 className={`w-5 h-5 flex-shrink-0 ${isActive('/admin/clients') ? 'text-[#c41e3a]' : ''}`} />
                      <span className="ml-3 truncate">Clients</span>
                    </Link>
                  )}
                  {(isSystemAdmin || canManageClients) && (
                    <Link
                      to="/admin/approvals"
                      onClick={handleNavClick}
                      className={`
                        flex items-center px-3 py-2.5 rounded-lg
                        text-sm font-medium transition-all duration-200 no-underline
                        ${
                          isActive('/admin/approvals')
                            ? 'bg-[#c41e3a]/10 text-[#c41e3a] border-l-3 border-[#c41e3a]'
                            : 'text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.04]'
                        }
                      `}
                      aria-current={isActive('/admin/approvals') ? 'page' : undefined}
                    >
                      <ShieldCheck className={`w-5 h-5 flex-shrink-0 ${isActive('/admin/approvals') ? 'text-[#c41e3a]' : ''}`} />
                      <span className="ml-3 truncate">Approvals</span>
                    </Link>
                  )}
                  {canAccessInvoicing && (
                    <Link
                      to="/invoicing"
                      onClick={handleNavClick}
                      className={`
                        flex items-center px-3 py-2.5 rounded-lg
                        text-sm font-medium transition-all duration-200 no-underline
                        ${
                          isActive('/invoicing')
                            ? 'bg-[#c41e3a]/10 text-[#c41e3a] border-l-3 border-[#c41e3a]'
                            : 'text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.04]'
                        }
                      `}
                      aria-current={isActive('/invoicing') ? 'page' : undefined}
                    >
                      <Receipt className={`w-5 h-5 flex-shrink-0 ${isActive('/invoicing') ? 'text-[#c41e3a]' : ''}`} />
                      <span className="ml-3 truncate">Invoicing</span>
                    </Link>
                  )}
                  {isSystemAdmin && systemAdminItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={handleNavClick}
                        className={`
                          flex items-center px-3 py-2.5 rounded-lg
                          text-sm font-medium transition-all duration-200 no-underline
                          ${
                            active
                              ? 'bg-[#c41e3a]/10 text-[#c41e3a] border-l-3 border-[#c41e3a]'
                              : 'text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.04]'
                          }
                        `}
                        aria-current={active ? 'page' : undefined}
                      >
                        <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-[#c41e3a]' : ''}`} />
                        <span className="ml-3 truncate">{item.name}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </>
          )}

          {/* Report Issue - Highlighted link for all users */}
          <div className="border-t border-black/[0.06] my-4 mx-2" />
          <div className="px-1">
            <Link
              to="/report-issue"
              onClick={handleNavClick}
              className={`
                flex items-center px-3 py-2.5 rounded-lg
                text-sm font-medium transition-all duration-200 no-underline
                ${
                  isActive('/report-issue')
                    ? 'bg-amber-500/20 text-amber-700 border border-amber-500/30'
                    : 'bg-amber-50 text-amber-700 border border-amber-200/50 hover:bg-amber-100 hover:border-amber-300'
                }
              `}
              aria-current={isActive('/report-issue') ? 'page' : undefined}
            >
              <MessageSquareWarning className="w-5 h-5 flex-shrink-0" />
              <span className="ml-3 truncate">Report Issue</span>
            </Link>
          </div>
        </div>

        {/* API Usage Widget - Fixed at bottom */}
        {SHOW_API_USAGE && apiUsage && (
          <div className="p-3 border-t border-black/[0.06] bg-white/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-[#64748b]">API Usage</span>
              <span className="text-xs font-semibold" style={{ color: usageColor }}>
                {apiUsage.used.toLocaleString()} / {(apiUsage.limit / 1000).toFixed(0)}K
              </span>
            </div>
            <div className="h-1.5 bg-black/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${usagePercentage}%`,
                  backgroundColor: usageColor,
                }}
              />
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
